// src/DashboardConcluidas.js
// Painel de indicadores. Busca as pendências concluídas uma vez (status == 'Concluído', sem índice
// composto) e calcula tudo na tela; as abertas vêm do quadro (prop tasks).
import React, { useState, useMemo } from 'react';
import { useConcluidas } from './concluidasStore';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, ArcElement } from 'chart.js';
import { PageHeader, Card, Campo, inputCls, normalizar, IconGrafico, BuscaSelect } from './ui';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ArcElement);

// ---------- cores (paleta categórica validada, sempre na mesma ordem) ----------
const COR_PRINCIPAL = '#2a78d6';
const CATEGORICAS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COR_OUTROS = '#b4b2ab';
const TEXTO = '#52514e';
const GRADE = 'rgba(0,0,0,0.06)';

// ---------- datas ----------
const textoParaData = (dt, hora) => {
    const m = String(dt || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return null;
    const [h, mi] = String(hora || '00:00').split(':').map(Number);
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), h || 0, mi || 0);
};
const tsParaData = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : null);
const dataConclusao = (t) => tsParaData(t.timestampconclusao) || textoParaData(t.dtconclusao) || tsParaData(t.timestampAlteracao) || textoParaData(t.dtAlteracao, t.horaAlteracao);
const dataAbertura = (t) => tsParaData(t.timestampCriacao) || textoParaData(t.dtLancamento, t.hora);
const diasEntre = (a, b) => (a && b ? Math.max(0, (b - a) / 86400000) : null);
const isoDia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PERIODOS = [
    { id: '30', label: '30 dias', range: () => { const f = new Date(); const i = new Date(); i.setDate(f.getDate() - 29); return [i, f]; } },
    { id: '90', label: '3 meses', range: () => { const f = new Date(); const i = new Date(f.getFullYear(), f.getMonth() - 2, 1); return [i, f]; } },
    { id: '365', label: '12 meses', range: () => { const f = new Date(); const i = new Date(f.getFullYear(), f.getMonth() - 11, 1); return [i, f]; } },
    { id: 'ano', label: 'Este ano', range: () => { const f = new Date(); return [new Date(f.getFullYear(), 0, 1), f]; } },
    { id: 'tudo', label: 'Tudo', range: () => [null, null] },
];

// ---------- opções dos gráficos ----------
const fonte = { family: 'inherit', size: 11 };
const opcoesBarras = (horizontal) => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (c) => ` ${c.parsed[horizontal ? 'x' : 'y']} concluída(s)` } },
    },
    scales: {
        [horizontal ? 'y' : 'x']: { grid: { display: false }, ticks: { color: TEXTO, font: fonte, autoSkip: !horizontal } },
        [horizontal ? 'x' : 'y']: { beginAtZero: true, ticks: { precision: 0, color: TEXTO, font: fonte }, grid: { color: GRADE }, border: { display: false } },
    },
});
const dadosBarras = (labels, valores) => ({
    labels,
    datasets: [{ data: valores, backgroundColor: COR_PRINCIPAL, borderRadius: 4, borderSkipped: 'start', maxBarThickness: 28 }],
});

// agrupa e ordena; o que passar de "max" vira "Outros"
const contarPor = (lista, chave, max) => {
    const m = {};
    lista.forEach((t) => { const k = chave(t) || 'Não informado'; m[k] = (m[k] || 0) + 1; });
    const ord = Object.entries(m).sort((a, b) => b[1] - a[1]);
    if (!max || ord.length <= max) return ord;
    const resto = ord.slice(max - 1).reduce((a, [, n]) => a + n, 0);
    return [...ord.slice(0, max - 1), ['Outros', resto]];
};

const Kpi = ({ rotulo, valor, detalhe, alerta }) => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{rotulo}</div>
        <div className={`text-3xl font-bold mt-0.5 ${alerta ? 'text-red-600' : 'text-gray-800'}`}>{valor}</div>
        {detalhe && <div className="text-xs text-gray-500 mt-0.5">{detalhe}</div>}
    </div>
);

const SemDados = () => <div className="h-full flex items-center justify-center text-sm text-gray-400">Sem dados no período</div>;

// ======================================================================
const DashboardConcluidas = ({ tasks = [], consultores = [], onViewPendency }) => {
    const { concluidas: brutas, carregando: loading, erro: erroCarga } = useConcluidas();
    const erro = erroCarga ? (erroCarga.code === 'resource-exhausted' ? 'A cota diária do Firebase foi atingida. Tente novamente mais tarde.' : 'Não foi possível carregar os dados.') : '';
    const concluidas = useMemo(() => brutas.map((t) => ({ ...t, _concl: dataConclusao(t), _abert: dataAbertura(t) })), [brutas]);
    const [periodo, setPeriodo] = useState('90');
    const [startDate, setStartDate] = useState(() => isoDia(PERIODOS[1].range()[0]));
    const [endDate, setEndDate] = useState(() => isoDia(new Date()));
    const [tecnico, setTecnico] = useState('');

    const aplicarPeriodo = (id) => {
        setPeriodo(id);
        const [i, f] = PERIODOS.find((p) => p.id === id).range();
        setStartDate(i ? isoDia(i) : '');
        setEndDate(f ? isoDia(f) : '');
    };

    const doTecnico = (t) => !tecnico || normalizar(t.tecnico) === normalizar(tecnico);

    const filtradas = useMemo(() => {
        const ini = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const fim = endDate ? new Date(`${endDate}T23:59:59.999`) : null;
        return concluidas.filter((t) => doTecnico(t) && (!ini || (t._concl && t._concl >= ini)) && (!fim || (t._concl && t._concl <= fim)));
    }, [concluidas, startDate, endDate, tecnico]); // eslint-disable-line react-hooks/exhaustive-deps

    const abertas = useMemo(() => tasks.filter(doTecnico), [tasks, tecnico]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------- indicadores ----------
    const kpis = useMemo(() => {
        const tempos = filtradas.map((t) => diasEntre(t._abert, t._concl)).filter((x) => x != null);
        const media = tempos.length ? tempos.reduce((a, b) => a + b, 0) / tempos.length : null;
        const hoje = new Date();
        const antigas = abertas.filter((t) => { const d = dataAbertura(t); return d && diasEntre(d, hoje) >= 15; }).length;
        const empresasAtendidas = new Set(filtradas.map((t) => t.empresaId || t.empresa)).size;
        return { total: filtradas.length, media, abertas: abertas.length, antigas, empresasAtendidas };
    }, [filtradas, abertas]);

    // ---------- série temporal: por dia (períodos curtos) ou por mês ----------
    const serie = useMemo(() => {
        const datas = filtradas.map((t) => t._concl).filter(Boolean);
        if (!datas.length) return null;
        const ini = startDate ? new Date(`${startDate}T00:00:00`) : new Date(Math.min(...datas));
        const fim = endDate ? new Date(`${endDate}T00:00:00`) : new Date(Math.max(...datas));
        const porDia = (fim - ini) / 86400000 <= 62;
        const chaves = [];
        const rotulos = [];
        if (porDia) {
            for (let d = new Date(ini.getFullYear(), ini.getMonth(), ini.getDate()); d <= fim; d.setDate(d.getDate() + 1)) {
                chaves.push(isoDia(d));
                rotulos.push(d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }));
            }
        } else {
            for (let d = new Date(ini.getFullYear(), ini.getMonth(), 1); d <= fim; d.setMonth(d.getMonth() + 1)) {
                chaves.push(`${d.getFullYear()}-${d.getMonth()}`);
                rotulos.push(d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', ''));
            }
        }
        const cont = Object.fromEntries(chaves.map((k) => [k, 0]));
        datas.forEach((d) => {
            const k = porDia ? isoDia(d) : `${d.getFullYear()}-${d.getMonth()}`;
            if (k in cont) cont[k] += 1;
        });
        return { titulo: porDia ? 'Concluídas por dia' : 'Concluídas por mês', rotulos, valores: chaves.map((k) => cont[k]) };
    }, [filtradas, startDate, endDate]);

    const porTecnico = useMemo(() => contarPor(filtradas, (t) => t.tecnico, 10), [filtradas]);
    const porCliente = useMemo(() => contarPor(filtradas, (t) => t.empresa, 10), [filtradas]);
    const porModulo = useMemo(() => contarPor(filtradas, (t) => t.modulo, 8), [filtradas]);
    const abertasPorStatus = useMemo(() => {
        const ordem = ['Pendente', 'Ag. Retorno', 'Em Atendimento', 'Ag. Pendencia', 'Atualizar'];
        const m = Object.fromEntries(ordem.map((s) => [s, 0]));
        abertas.forEach((t) => { if (t.status in m) m[t.status] += 1; });
        return ordem.map((s) => [s, m[s]]);
    }, [abertas]);

    const tempoPorTecnico = useMemo(() => {
        const m = {};
        filtradas.forEach((t) => {
            const d = diasEntre(t._abert, t._concl);
            if (d == null) return;
            const k = t.tecnico || 'Não informado';
            m[k] = m[k] || { soma: 0, n: 0 };
            m[k].soma += d; m[k].n += 1;
        });
        return Object.entries(m).map(([k, v]) => [k, v.soma / v.n, v.n]).sort((a, b) => b[2] - a[2]).slice(0, 10);
    }, [filtradas]);

    const corDoughnut = porModulo.map(([k], i) => (k === 'Outros' ? COR_OUTROS : CATEGORICAS[i % CATEGORICAS.length]));
    const altura = (n) => Math.max(180, n * 30 + 40);

    return (
        <div className="max-w-7xl mx-auto space-y-4">
            <PageHeader icon={IconGrafico} cor="amber" titulo="Dashboard" subtitulo="Indicadores das pendências concluídas e em aberto">
                {onViewPendency && (
                    <button type="button" onClick={() => onViewPendency('pendencias_concluidas')} className="h-9 px-3 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
                        Ver lista de concluídas
                    </button>
                )}
            </PageHeader>

            {/* ---------- filtros (uma linha) ---------- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap items-end gap-3">
                <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Período (conclusão)</span>
                    <div className="inline-flex flex-wrap rounded-md border border-gray-300 overflow-hidden">
                        {PERIODOS.map((p) => (
                            <button key={p.id} type="button" onClick={() => aplicarPeriodo(p.id)}
                                className={`px-3 h-9 text-sm border-r last:border-r-0 border-gray-300 ${periodo === p.id ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
                <Campo rotulo="De"><input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPeriodo('custom'); }} className={inputCls} /></Campo>
                <Campo rotulo="Até"><input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPeriodo('custom'); }} className={inputCls} /></Campo>
                <Campo rotulo="Técnico" className="min-w-[12rem]">
                    <BuscaSelect placeholder="Todos os técnicos" vazio="Nenhum técnico encontrado"
                        valor={tecnico} onChange={setTecnico}
                        opcoes={consultores.map((c) => ({ valor: c.nome, rotulo: c.nome || '', detalhe: c.ativo === false ? 'inativo' : '' }))} />
                </Campo>
            </div>

            {loading ? (
                <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">Carregando indicadores...</div>
            ) : erro ? (
                <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-red-600">{erro}</div>
            ) : (
                <>
                    {/* ---------- indicadores ---------- */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi rotulo="Concluídas no período" valor={kpis.total} detalhe={`${kpis.empresasAtendidas} empresa${kpis.empresasAtendidas === 1 ? '' : 's'} atendida${kpis.empresasAtendidas === 1 ? '' : 's'}`} />
                        <Kpi rotulo="Tempo médio até concluir" valor={kpis.media == null ? '—' : `${kpis.media.toFixed(1).replace('.', ',')}d`} detalhe="da abertura à conclusão" />
                        <Kpi rotulo="Em aberto agora" valor={kpis.abertas} detalhe="no quadro de pendências" />
                        <Kpi rotulo="Abertas há 15+ dias" valor={kpis.antigas} alerta={kpis.antigas > 0} detalhe={kpis.antigas > 0 ? 'precisam de atenção' : 'nenhuma atrasada'} />
                    </div>

                    {/* ---------- evolução ---------- */}
                    <Card titulo={serie ? serie.titulo : 'Concluídas no período'}>
                        <div className="h-64">
                            {serie ? <Bar options={opcoesBarras(false)} data={dadosBarras(serie.rotulos, serie.valores)} /> : <SemDados />}
                        </div>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card titulo="Concluídas por técnico">
                            <div style={{ height: altura(porTecnico.length) }}>
                                {porTecnico.length ? <Bar options={opcoesBarras(true)} data={dadosBarras(porTecnico.map(([k]) => k), porTecnico.map(([, n]) => n))} /> : <SemDados />}
                            </div>
                        </Card>

                        <Card titulo="Concluídas por módulo">
                            <div className="h-64">
                                {porModulo.length ? (
                                    <Doughnut
                                        data={{ labels: porModulo.map(([k]) => k), datasets: [{ data: porModulo.map(([, n]) => n), backgroundColor: corDoughnut, borderColor: '#ffffff', borderWidth: 2 }] }}
                                        options={{
                                            responsive: true, maintainAspectRatio: false, cutout: '62%',
                                            plugins: {
                                                legend: { position: 'right', labels: { color: TEXTO, font: fonte, boxWidth: 12, boxHeight: 12 } },
                                                tooltip: { callbacks: { label: (c) => ` ${c.label}: ${c.parsed} (${Math.round((c.parsed / kpis.total) * 100)}%)` } },
                                            },
                                        }}
                                    />
                                ) : <SemDados />}
                            </div>
                        </Card>

                        <Card titulo="Clientes com mais pendências concluídas">
                            <div style={{ height: altura(porCliente.length) }}>
                                {porCliente.length ? <Bar options={opcoesBarras(true)} data={dadosBarras(porCliente.map(([k]) => k), porCliente.map(([, n]) => n))} /> : <SemDados />}
                            </div>
                        </Card>

                        <Card titulo="Em aberto agora, por status">
                            <div className="h-64">
                                <Bar options={opcoesBarras(false)} data={dadosBarras(abertasPorStatus.map(([k]) => k), abertasPorStatus.map(([, n]) => n))} />
                            </div>
                        </Card>
                    </div>

                    {/* ---------- tabela: tempo médio por técnico ---------- */}
                    <Card titulo="Tempo médio até concluir, por técnico" semPadding>
                        {tempoPorTecnico.length === 0 ? (
                            <div className="p-6 text-center text-sm text-gray-400">Sem dados no período</div>
                        ) : (
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-2 text-left">Técnico</th>
                                        <th className="px-4 py-2 text-right">Concluídas</th>
                                        <th className="px-4 py-2 text-right">Tempo médio</th>
                                        <th className="px-4 py-2 w-1/3 hidden sm:table-cell" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {(() => {
                                        const maior = Math.max(...tempoPorTecnico.map(([, m]) => m), 1);
                                        return tempoPorTecnico.map(([nome, media, n]) => (
                                            <tr key={nome}>
                                                <td className="px-4 py-2 font-medium text-gray-800">{nome}</td>
                                                <td className="px-4 py-2 text-right text-gray-700">{n}</td>
                                                <td className="px-4 py-2 text-right text-gray-700">{media.toFixed(1).replace('.', ',')} dias</td>
                                                <td className="px-4 py-2 hidden sm:table-cell">
                                                    <div className="h-2 rounded-full bg-gray-100">
                                                        <div className="h-2 rounded-full" style={{ width: `${(media / maior) * 100}%`, background: COR_PRINCIPAL }} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </>
            )}
        </div>
    );
};

export default DashboardConcluidas;
